<template>
  <div class="application-progress">

    <!-- Desktop Navigation -->
    <div v-if="isInApplicationFlow && !isMobileBrowser && wideViewport">
      <div class="row"
        :id="'section' + index"
        v-for="(section, index) in sectionList"
        :key="index">
        <!-- Increase index by one for rest of function -->
        <div :class="['col', sectionIsClickable(index) ? 'clickable' : '']"
             @click="routeToSection(section, index)">
          <div class="row align-items-center">
            <div class="col-4 navLeftColumn">

              <div v-if="sectionIsAppraisal(index) || (!showCompleteBubbleDesktop(index) && getFurthestSectionIndex === index && getFurthestSectionIndex !== 4)"
                   class="circleNav icon--nav-circle">
                <div class="circularProgressContainer">
                  <div class="circularText">{{ index + 1 }}</div>
                  <div class="circularProgressLine"
                       :style="maskPercentCompleteSection"></div>
                  <div class="circularProgressBackground"></div>
                </div>
              </div>

              <ProgressBarPendingIcon2 v-else-if="getNavigationImage(index) === 'imageProgressPending2'"
                                       class="icon--nav-circle"/>
              <ProgressBarPendingIcon3 v-else-if="getNavigationImage(index) === 'imageProgressPending3'"
                                       class="icon--nav-circle"/>
              <ProgressBarPendingIcon4 v-else-if="getNavigationImage(index) === 'imageProgressPending4'"
                                       class="icon--nav-circle"/>
              <ProgressBarCompleteIcon v-else-if="showCompleteBubbleDesktop(index) || getNavigationImage(index) === 'imageProgressCompleted'"
                                       class="icon--nav-circle"/>

            </div>
            <div class="col-8 navRightColumn pl-4">
              <span class="text-22 text-600"
                    :class="getTextClass(index)">
                {{ $t(section) }}
              </span>
            </div>
          </div>

          <div v-if="index < sectionList.length - 1"
               class="navSpacer"></div>
          <div v-if="index < sectionList.length - 1"
               class="row">
            <div class="col-4 navLeftColumn">
              <div class="navLine"
                   :class="getNavLineClass(index)"></div>
            </div>
            <div class="col-8">
              <span class="text-22 text-600"></span>
            </div>
          </div>

          <div v-if="index < sectionList.length - 1"
               class="navSpacer"></div>
        </div>
      </div>
    </div>

    <!-- Mobile Navigation -->
    <div v-if="isInApplicationFlow && (isMobileBrowser || !wideViewport)">
      <div v-for="(section, index) in sectionList"
           class="navContent"
           :id="'section' + index"
           :key="index">
        <!-- Increase index by one for rest of function -->
        <div :class="[sectionIsClickable(index) ? 'clickable' : '', 'navContent']"
             @click="routeToSection(section, index)">
          <!-- Future Progress -->
          <div v-if="getFurthestSectionIndex < index">
            <ProgressBarPendingIcon2 v-if="getNavigationImage(index) === 'imageProgressPending2'"
                                     class="icon--nav"/>
            <ProgressBarPendingIcon3 v-if="getNavigationImage(index) === 'imageProgressPending3'"
                                     class="icon--nav"/>
            <ProgressBarPendingIcon4 v-if="getNavigationImage(index) === 'imageProgressPending4'"
                                     class="icon--nav"/>
          </div>
          <!-- Current Progress -->
          <div v-else-if="sectionIsAppraisal(index) || (!showCompletedBubble(index) && getFurthestSectionIndex === index)"
               class="navTextBubble">
            <div class="bubbleProgressContainer">
              <div class="bubbleProgressTop"
                   :style="clipPercentCompleteSection">
                <div class="bubbleTextTop bubbleText">
                  {{ $t(section) }}
                </div>
              </div>
              <div class="bubbleProgressBottom">
                <div class="bubbleTextBottom bubbleText">
                  {{ $t(section) }}
                </div>
              </div>
            </div>
          </div>
          <!-- Finished Progress -->
          <div v-else-if="showCompletedBubble(index)"
               class="bubbleCompleted">
            <ProgressBarCompleteIcon />
          </div>
          <div v-else
               class="navTextBubble bubbleProgressContainer">
            <span class="navBubbleText navBubbleTextPast">
              {{ $t(section) }}
            </span>
          </div>
        </div>

        <div v-if="index < sectionList.length - 1"
             class="navLineMobile"
             :class="getNavLineClass(index)"></div>
      </div>
    </div>

  </div>
</template>

<script>
// Components
import ProgressBarPendingIcon2 from '../icons/ProgressBarPendingIcon2.vue'
import ProgressBarPendingIcon3 from '../icons/ProgressBarPendingIcon3.vue'
import ProgressBarPendingIcon4 from '../icons/ProgressBarPendingIcon4.vue'
import ProgressBarCompleteIcon from '../icons/ProgressBarCompleteIcon.vue'

// Constants
import { AnalyticsConstants, LoanProductTypes } from '../../../constants'

// Libraries
import { mapGetters, mapState } from 'vuex'

// Utilities
import Analytics from '../../../utility/analyticsUtil'
import Logger from '../../../utility/loggerUtil'
import Router from '../../../utility/routerUtil'

// Resource Arrays
const imageProgressPending = [
  'imageProgressPending2',
  'imageProgressPending3',
  'imageProgressPending4'
]

const imageProgressCurrent = [
  'imageProgressCurrent1',
  'imageProgressCurrent2',
  'imageProgressCurrent3',
  'imageProgressCurrent4'
]

const titleSectionList = [
  'personal',
  'financial',
  'vehicle'
]

const signatureSectionList = [
  'personal',
  'financial',
  'vehicle'
]

export default {
  name: 'application-progress',

  components: {
    ProgressBarPendingIcon2,
    ProgressBarPendingIcon3,
    ProgressBarPendingIcon4,
    ProgressBarCompleteIcon
  },

  data () {
    return {
      navigatedSections: [],
      currentNavProgress: { personal: 0, financial: 0, vehicle: 0, banking: 0 }
    }
  },

  computed: {
    isInApplicationFlow () {
      return Router.isRouteInFlow('Application', this.$route)
    },

    sectionList () {
      // This adds either an appraisal step to the final screen
      if (this.currentSection && !signatureSectionList.includes(this.currentSection.toLowerCase()) && (this.currentSection.toLowerCase() === 'appraisal' || this.currentSection.toLowerCase() === 'appointment')) {
        titleSectionList.push(this.currentSection.toLowerCase())
        signatureSectionList.push(this.currentSection.toLowerCase())
      }

      if (this.loanProductType === LoanProductTypes.signatureLoan && signatureSectionList.includes('vehicle')) {
        signatureSectionList.splice(signatureSectionList.indexOf('vehicle'), 1, 'banking')
      } else if (this.loanProductType === LoanProductTypes.titleLoan && signatureSectionList.includes('banking')) {
        signatureSectionList.splice(signatureSectionList.indexOf('banking'), 1, 'vehicle')
      }

      return (this.isInstaLoanBrand ? signatureSectionList : titleSectionList) || titleSectionList
    },

    getCurrentSectionIndex () {
      if (!this.currentSection) {
        return 0
      }

      return this.sectionList.indexOf(this.currentSection.toLowerCase())
    },

    getFurthestSectionIndex () {
      if (this.navigatedSections.includes('appraisal')) {
        return this.sectionList.length - 1
      }

      if (this.navigatedSections.includes('complete')) {
        return this.sectionList.length + 1
      }

      let furthestIndex = 0

      this.navigatedSections.forEach(element => {
        if (this.sectionList.indexOf(element.toLowerCase()) > furthestIndex) {
          furthestIndex = this.sectionList.indexOf(element.toLowerCase())
        }
      })

      return furthestIndex
    },

    furthestSectionName () {
      return this.sectionList[this.getFurthestSectionIndex]
    },

    furthestSectionIsAppraisal () {
      return this.furthestSectionName === 'appraisal'
    },

    mobilePercentCompleteSection () {
      if (this.furthestSectionIsAppraisal) {
        return '0%'
      }

      return this.percentCompleteSection
    },

    desktopPercentCompleteSection () {
      if (this.furthestSectionIsAppraisal) {
        return '100%'
      }

      return this.percentCompleteSection
    },

    percentCompleteSection () {
      if (this.currentSection.toLowerCase() === 'complete' || this.isAwaitingSignature) {
        return '100%'
      }

      return `${this.currentNavProgress[this.furthestSectionName]}%`
    },

    maskPercentCompleteSection () {
      if (this.furthestSectionIsAppraisal) {
        return {
          '--mask-percentage': 'conic-gradient(black 100%, transparent 0%);'
        }
      }

      return {
        '--mask-percentage': this.percentCompleteSection
      }
    },

    clipPercentCompleteSection () {
      if (this.furthestSectionIsAppraisal) {
        return {
          'clip-path': 'inset(0 0 0 0%)'
        }
      }

      return {
        'clip-path': `inset(0 0 0 ${this.percentCompleteSection})`
      }
    },

    loanProductType () {
      return this.loanProduct !== undefined ? this.loanProduct : LoanProductTypes.titleLoan
    },

    currentSection () {
      // the furthest section is set in the router watch function below
      if (this.$route.meta?.section === undefined) {
        return ''
      }
      return this.$route.meta?.section.toLowerCase()
    },

    ...mapGetters('applicationSession', ['isInstaLoanBrand']),
    ...mapGetters('session', ['isAwaitingSignature']),
    ...mapState('display', ['isMobileBrowser', 'wideViewport']),
    ...mapState('applicationCustomer', ['loanProduct'])
  },

  watch: {
    '$route' (to, from) {
      if (this.$route?.meta?.section && !['NONE'].includes(this.$route.meta.section) && !this.navigatedSections.includes(this.$route.meta.section)) {
        this.navigatedSections.push(this.$route.meta.section.toLowerCase())
      }

      const currentFlowName = this.$store.state.applicationCustomer.newCustomer ? 'newCustomerFlow' : 'returnCustomerFlow'
      const newProgressionPercentage = this.$route.meta?.progressionPercentage?.[currentFlowName]

      this.currentNavProgress[this.currentSection] = newProgressionPercentage > this.currentNavProgress[this.currentSection] ? newProgressionPercentage : this.currentNavProgress[this.currentSection]
    }
  },

  methods: {
    // All methods must consider the current section, past sections, and future sections CSS and images
    getNavigationImage (sectionIndex) {
      const pendingSections = sectionIndex < this.getFurthestSectionIndex ? 'imageProgressCompleted' : imageProgressPending[sectionIndex - 1]
      return sectionIndex === this.getFurthestSectionIndex ? imageProgressCurrent[this.getFurthestSectionIndex] : pendingSections
    },

    getNavLineClass (sectionNumber) {
      return sectionNumber >= this.getFurthestSectionIndex ? 'navLineFuture' : 'navLinePast'
    },

    getTextClass (sectionNumber) {
      return sectionNumber === this.getCurrentSectionIndex ? 'navTextCurrent' : this.showCompletedBubble(sectionNumber) || (sectionNumber < this.getFurthestSectionIndex) ? 'navTextPast' : 'navTextFuture'
    },

    sectionIsClickable (sectionNumber) {
      return !this.showCompletedBubble(sectionNumber) && sectionNumber <= this.getFurthestSectionIndex
    },

    showCompleteBubbleDesktop (sectionNumber) {
      if (this.currentSection && this.currentSection.toLowerCase() === 'complete') {
        return true
      }

      return this.showCompletedBubble(sectionNumber)
    },

    showCompletedBubble (sectionNumber) {
      if (this.sectionIsAppraisal(sectionNumber)) {
        return false
      }

      return this.currentSection?.toLowerCase() === 'appraisal'
    },

    sectionIsAppraisal (sectionNumber) {
      return this.sectionList[sectionNumber] === 'appraisal'
    },

    routeToSection (section, sectionNumber) {
      if (!this.sectionIsClickable(sectionNumber)) {
        return
      }

      const applicationRoutes = this.$router.options.routes.find(route => route?.meta?.flow === 'Application')
      const sectionStartingPage = applicationRoutes.children.find(route => route?.meta?.section?.toLowerCase() === section && route.meta?.sectionStartingPage)

      if (!sectionStartingPage) {
        Logger.debug('Section starting page not found')
        return
      }

      Analytics.logEvent(AnalyticsConstants.eventName.selectItem, { item_id: 'navigation_bar', value: sectionStartingPage.name })
      this.$store.commit('applicationSession/setEditMode', { editMode: true, editOrigin: this.$router.currentRoute.name })
      this.$store.commit('applicationSession/setIdScanManualEntry', { idScanManualEntry: false })
      this.$router.push({ name: sectionStartingPage.name }).catch(error => Logger.warn(`Navigation blocked: ${error}`))
    }
  }
}
</script>

<style scoped lang="scss">
@use '../../../assets/styles/colors';

.application-progress {
  margin-top: 0.375rem;
}

.navTextBubble {
  height: 1.375rem;
  line-height: 1.125rem;
  text-align: center;
  border-radius: 12px;
}

.navBubbleText {
  padding: 0 0.625rem 0 0.625rem;
  font-size: 0.75rem;
  line-height: 1.25rem;
  text-align: center;
}

.navLeftColumn {
  padding: 0rem;
  text-align: right;
}

.navRightColumn {
  padding: 0rem;
  text-align: left;
}

.navLine {
  float: right;
  width: 0.0625rem;
  min-height: 2.5rem;
  margin-right: 1rem;
  object-fit: contain;
}

.navSpacer {
  min-height: 0.625rem;
}

.navLineMobile {
  float: none;
  display: inline-block;
  min-width: 1.125rem;
  min-height: 0rem;
  margin: 0rem 0.25rem 0rem 0.25rem;
  vertical-align: middle;
  object-fit: contain;
  border-width: 1px !important;
}

.navContent {
  display: inline-block;
}

.icon--nav-circle {
  width: 2rem;
  height: 2rem;
}

.icon--nav {
  width: 1.25rem;
  height: 1.25rem;
}

.clickable {
  cursor: pointer;
}

.circleNav {
  display: flex;
  justify-content: flex-end;
  width: 100%;
  height: 2rem;
}

.circularProgressContainer {
  display: grid;
  min-width: 2rem;
  min-height: 2rem;
  overflow: hidden;
}

.circularText {
  z-index: 4;
  width: 100%;
  line-height: 1.75rem;
  text-align: center;
}

.circularProgressLine {
  position: absolute;
  z-index: 3;
  width: 1.875rem;
  aspect-ratio: 1;
  border-radius: 100%;
  -webkit-mask: conic-gradient(black var(--mask-percentage), transparent 0%);
  mask: conic-gradient(black var(--mask-percentage), transparent 0%);
}

.circularProgressBackground {
  position: absolute;
  z-index: 2;
  width: 30px;
  aspect-ratio: 1;
  border-radius: 100%;
}

.bubbleProgressContainer {
  display: grid;
  width: max-content;
  height: 1.25rem;
  overflow: hidden;
  border-radius: 0.625rem;
}

.bubbleProgressTop, .bubbleProgressBottom {
  grid-row: 1;
  grid-column: 1;
  padding: 0 5px 0 5px;
}

.bubbleText {
  font-size: .75rem;
  line-height: 1.1875rem;
  text-align: center;
}

.bubbleProgressTop {
  z-index: 2;
  grid-row: 1;
  grid-column: 1;
  background-color: colors.$white;
}

.bubbleProgressBottom {
  z-index: 1;
  color: colors.$white;
}

.bubbleTextBottom {
  padding: 0 .625rem 0 .625rem;
  font-size: 12px;
  line-height: 1.25rem;
  color: colors.$white;
  text-align: center;
}
</style>

<i18n>
  {
    "en": {
      "personal": "Personal",
      "financial": "Financial",
      "vehicle": "Vehicle",
      "banking": "Banking",
      "appraisal": "Appraisal"
    }
  }
</i18n>
